<template>
  <div class="d-flex flex-column" :style="{ width }">
    <label v-if="label" :class="{ 'label-error': error }">
      {{ label }}
      <span v-if="labelRequired">*</span>
    </label>
    <div
      class="input-wrapper d-flex align-center"
      :class="{ focus, disabled, 'input-error': error }"
      :style="{ height, 'border-radius': radius }"
      v-click-outside="blur"
      @click="focusOn"
    >
      <!-- prepend icon -->
      <div
        v-if="prependIcon"
        class="img-prepend d-flex align-center"
        @click="focusOn"
      >
        <img
          v-if="disabled"
          :src="prependDisabledIcon"
          width="18px"
          height="18px"
          draggable="false"
          alt="icon"
        />
        <img v-else :src="prependIcon" width="18px" height="18px" alt="icon" />
      </div>
      <!-- prepend icon -->

      <span v-if="readonly">{{ value }}</span>
      <input
        v-else
        ref="input"
        v-model="inputValue"
        :type="passwordShow ? 'text' : type"
        :placeholder="placeholder"
        :max="max"
        :min="min"
        @keypress.enter="enter"
      />

      <!-- append/password icon -->
      <div
        v-if="type === 'password' && usePasswordIcon"
        class="img-append d-flex align-center"
        style="cursor: pointer"
        @click="passwordShow = !passwordShow"
      >
        <img
          :src="passwordShow ? '/img/eye_open.svg' : '/img/eye_close.svg'"
          width="18px"
          height="18px"
          draggable="false"
          alt="icon-eye"
        />
      </div>
      <div
        v-else-if="appendIcon"
        class="img-append d-flex align-center"
        @click="focusOn"
      >
        <img
          v-if="disabled"
          :src="appendDisabledIcon"
          width="18px"
          height="18px"
          draggable="false"
          alt="icon"
        />
        <img v-else :src="appendIcon" width="18px" height="18px" alt="icon" />
      </div>
      <!-- append/password icon -->
    </div>

    <div v-if="errorMsg" class="error-msg" :style="{ 'text-align': msgAlign }">
      {{ errorMsg }}
    </div>
    <div
      v-else-if="$slots.msg"
      class="slot-msg"
      :style="{ 'text-align': msgAlign }"
    >
      <slot name="msg"></slot>
    </div>
  </div>
</template>

<script>
import util from "@/common/util";

export default {
  props: {
    value: {
      type: [String, Number],
      default: () => "",
    },
    /** input type (text, number, password, number-string) */
    type: {
      type: String,
      default: () => "text",
    },
    /** input label (상단) */
    label: {
      type: String,
      default: () => "",
    },
    /** label required (*) */
    labelRequired: {
      type: Boolean,
      default: () => false,
    },
    /** input placeholder */
    placeholder: {
      type: String,
      default: () => "",
    },
    /** input 넓이 */
    width: {
      type: String,
      default: () => "100%",
    },
    /** input 높이 */
    height: {
      type: String,
      default: () => "40px",
    },
    /** input border radius */
    radius: {
      type: String,
      default: () => "10px",
    },
    /** input 비활성화 */
    disabled: {
      type: Boolean,
      default: () => false,
    },
    /** input readonly (보기 모드) */
    readonly: {
      type: Boolean,
      default: () => false,
    },
    /** 인풋 앞쪽 아이콘 */
    prependIcon: {
      type: String,
      default: () => "",
    },
    /** 인풋 앞쪽 비활성화 시 아이콘 */
    prependDisabledIcon: {
      type: String,
      default: () => "",
    },
    /**
     * 인풋 뒤쪽 아이콘
     * !!!! type password 인 경우 설정 불가
     */
    appendIcon: {
      type: String,
      default: () => "",
    },
    /** 인풋 뒤쪽 비활성화 시 아이콘 */
    appendDisabledIcon: {
      type: String,
      default: () => "",
    },
    /** 메시지 정렬 방향 (일반 메시지, 에러 메시지)
     * left, right, center
     */
    msgAlign: {
      type: String,
      default: () => "left",
    },
    /** 에러 여부 */
    error: {
      type: Boolean,
      default: () => false,
    },
    /** 에러 메시지 */
    errorMsg: {
      type: String,
      default: () => "",
    },
    /** 최댓값 */
    max: {
      type: String,
      default: () => "",
    },
    /** 최솟값 */
    min: {
      type: String,
      default: () => "",
    },
    maxLength: {
      type: String,
      default: () => "",
    },
    usePasswordIcon: {
      type: Boolean,
      default: () => true,
    },
  },
  data: () => {
    return {
      focus: false,
      inputValue: "",
      passwordShow: false,
    };
  },
  mounted() {
    this.inputValue = this.value;
  },
  watch: {
    value() {
      this.inputValue = this.value;
    },
    inputValue() {
      if (parseInt(this.maxLength) > 0) {
        this.inputValue = this.inputValue.substring(
          0,
          parseInt(this.maxLength)
        );
      }

      if (this.type === "number-string") {
        this.inputValue = this.getNumberString(this.inputValue);

        this.$emit("input", this.inputValue);
        return;
      }

      this.$emit("input", this.inputValue);
    },
  },
  methods: {
    focusOn() {
      if (this.disabled || this.readonly || this.focus) return;
      this.$refs.input.focus();
      this.focus = true;
      this.$emit("focus", this.inputValue);
    },
    blur() {
      if (this.disabled || this.readonly || !this.focus) return;

      this.focus = false;
      if (this.type === "number" && this.inputValue) {
        this.inputValue = this.getNumber(this.inputValue);
      }
      this.$emit("blur", this.inputValue);
    },
    enter() {
      this.$emit("enter", this.inputValue);
    },
    getNumber(n) {
      if (this.max && n > this.max) n = this.max;
      if (this.min && n < this.min) n = this.min;
      return n;
    },
    getNumberString(value) {
      return this.getNumberComma(value).toString();
    },
    getNumberComma(value) {
      value = String(value);
      const REGEX_NOT_A_NUMBER = /[^0-9]/g;
      value = value.replace(REGEX_NOT_A_NUMBER, "");

      if (value === "") {
        return "0";
      }

      value = parseFloat(value) || 0;

      if (this.max && this.max < value) {
        value = this.max;
      }
      if (this.min && this.min > value) {
        value = this.min;
      }

      return util.comma(value);
    },
  },
};
</script>

<style lang="scss" scoped>
label {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 133%;
  color: var(--m-gray01);

  &.label-error {
    color: var(--s-red00);
  }

  & > span {
    color: var(--s-red00);
    font-size: 12px;
    line-height: 133%;
  }
}

.input-wrapper {
  position: relative;
  padding: 10.5px 12px;
  border: 1px solid var(--m-gray03);
  background-color: var(--m-bg);
  transition: 0.1s;

  &.focus {
    border: 1px solid var(--p-blue00);

    & > input {
      caret-color: var(--p-blue00);
    }
  }

  &.disabled {
    & > * {
      pointer-events: none;
      user-select: none;
    }

    & > input,
    & > input::placeholder {
      color: var(--m-gray02);
    }
  }

  &:hover:not(.disabled) {
    background-color: var(--m-gray04);
  }

  &.input-error {
    border: 1px solid var(--s-red00);

    & > input::placeholder {
      color: var(--s-red00);
    }
  }

  & > .img-prepend {
    margin-right: 6px;
  }

  & > input {
    width: 100%;
    height: 100%;
    outline: none;

    &::placeholder {
      color: var(--m-gray01);
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  & > .img-append {
    margin-left: 6px;
  }
}

.slot-msg {
  margin-top: 4px;
  width: 100%;
  font-size: 12px;
  line-height: 133%;
  color: var(--m-gray01);
}

.error-msg {
  margin-top: 4px;
  width: 100%;
  font-size: 12px;
  line-height: 133%;
  color: var(--s-red00);
}
</style>
